/**
 * Step5_Review Component
 *
 * Final step of the wizard - Review all entered data before submission.
 *
 * @module components/Step5_Review
 */

import React from 'react';
import {
  FileText,
  AlertTriangle,
  Shield,
  Calendar,
  MapPin,
  Users,
  ArrowRight,
  Edit2,
  CheckCircle,
  Clock
} from 'lucide-react';
import Dropdown from '../../../components/common/Dropdown';
import { RiskLevelBadge } from './RiskMatrix';
import {
  getRiskLevel,
  getAlarpZone,
  ASSESSMENT_TYPES,
  REVIEW_FREQUENCIES,
  CONTROL_HIERARCHY
} from '../config/moduleConfig';

/**
 * Get background color for risk level
 */
const getRiskBgColor = (score) => {
  if (score >= 20) return 'bg-red-100 border-red-200';
  if (score >= 12) return 'bg-orange-100 border-orange-200';
  if (score >= 6) return 'bg-yellow-100 border-yellow-200';
  return 'bg-green-100 border-green-200';
};

/**
 * Step5_Review Component
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.setFormData - Function to update form data
 * @param {Function} props.goToStep - Function to navigate to a specific step
 */
export const Step5_Review = ({ formData, setFormData, goToStep }) => {
  const hazards = formData.hazards || [];

  // Calculate risk statistics
  const riskStats = {
    extreme: 0,
    high: 0,
    medium: 0,
    low: 0
  };

  let totalInitialRisk = 0;
  let totalResidualRisk = 0;

  hazards.forEach(hazard => {
    const initialScore = (hazard.initialLikelihood || 1) * (hazard.initialSeverity || 1);
    const residualScore = (hazard.residualLikelihood || 1) * (hazard.residualSeverity || 1);
    const level = getRiskLevel(residualScore);
    riskStats[level]++;
    totalInitialRisk += initialScore;
    totalResidualRisk += residualScore;
  });

  const overallReduction = totalInitialRisk > 0
    ? Math.round(((totalInitialRisk - totalResidualRisk) / totalInitialRisk) * 100)
    : 0;

  // Check for required actions
  const actionsRequired = hazards.filter(h => {
    const score = (h.residualLikelihood || 1) * (h.residualSeverity || 1);
    return score >= 6 && h.actionRequired;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Review Assessment</h3>
        <p className="text-sm text-gray-600 mt-1">
          Review all details before submitting. Click any section's Edit button to make changes.
        </p>
      </div>

      {/* Assessment Details Card */}
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="bg-slate-700 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-white" />
            <h4 className="text-base font-semibold text-white">Assessment Details</h4>
          </div>
          <button
            onClick={() => goToStep?.(1)}
            className="text-sm text-white hover:text-cyan-200 flex items-center gap-1"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="p-6">
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Title</dt>
              <dd className="mt-1 text-sm text-gray-900 font-medium">
                {formData.title || 'Untitled Assessment'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Type</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {ASSESSMENT_TYPES[formData.type]?.label || 'Generic'}
              </dd>
            </div>
            {formData.description && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{formData.description}</dd>
              </div>
            )}
            {formData.location && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  <MapPin className="h-4 w-4 inline mr-1" />
                  Location
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{formData.location}</dd>
              </div>
            )}
            {formData.department && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  <Users className="h-4 w-4 inline mr-1" />
                  Department
                </dt>
                <dd className="mt-1 text-sm text-gray-900">{formData.department}</dd>
              </div>
            )}
            {formData.selectedTask && (
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Based on Workboat Task</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {formData.selectedTask.title}
                  <span className="text-gray-500 ml-2">({formData.selectedTask.wbc3Reference})</span>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Risk Summary Card */}
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-white" />
            <h4 className="text-base font-semibold text-white">Risk Summary</h4>
          </div>
        </div>
        <div className="p-6">
          {/* Risk Level Distribution */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className={`p-4 rounded-lg border ${getRiskBgColor(25)}`}>
              <p className="text-3xl font-bold text-gray-900">{riskStats.extreme}</p>
              <p className="text-sm font-medium text-red-800">Extreme</p>
            </div>
            <div className={`p-4 rounded-lg border ${getRiskBgColor(15)}`}>
              <p className="text-3xl font-bold text-gray-900">{riskStats.high}</p>
              <p className="text-sm font-medium text-orange-800">High</p>
            </div>
            <div className={`p-4 rounded-lg border ${getRiskBgColor(8)}`}>
              <p className="text-3xl font-bold text-gray-900">{riskStats.medium}</p>
              <p className="text-sm font-medium text-yellow-800">Medium</p>
            </div>
            <div className={`p-4 rounded-lg border ${getRiskBgColor(3)}`}>
              <p className="text-3xl font-bold text-gray-900">{riskStats.low}</p>
              <p className="text-sm font-medium text-green-800">Low</p>
            </div>
          </div>

          {/* Overall Risk Reduction */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <p className="text-sm text-gray-600">Total Initial Risk</p>
              <p className="text-xl font-bold text-gray-900">{totalInitialRisk}</p>
            </div>
            <ArrowRight className="h-6 w-6 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-gray-600">Total Residual Risk</p>
              <p className="text-xl font-bold text-green-700">{totalResidualRisk}</p>
            </div>
            <div className="flex-1 text-right">
              <p className="text-sm text-gray-600">Risk Reduction</p>
              <p className="text-xl font-bold text-green-600">-{overallReduction}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hazards List Card */}
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="bg-slate-700 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-white" />
            <h4 className="text-base font-semibold text-white">
              Hazards ({hazards.length})
            </h4>
          </div>
          <button
            onClick={() => goToStep?.(2)}
            className="text-sm text-white hover:text-cyan-200 flex items-center gap-1"
          >
            <Edit2 className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="divide-y divide-gray-200">
          {hazards.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>No hazards identified</p>
            </div>
          ) : (
            hazards.map((hazard, index) => {
              const initialScore = (hazard.initialLikelihood || 1) * (hazard.initialSeverity || 1);
              const residualScore = (hazard.residualLikelihood || 1) * (hazard.residualSeverity || 1);
              const controlLevel = CONTROL_HIERARCHY[hazard.controlHierarchyLevel || 'ppe'];

              return (
                <div key={hazard.id} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {index + 1}. {hazard.hazardDescription}
                      </p>
                      {hazard.whoAffected && (
                        <p className="text-sm text-gray-600 mt-1">
                          <Users className="h-3 w-3 inline mr-1" />
                          {hazard.whoAffected}
                        </p>
                      )}
                      <div className="mt-2 space-y-1">
                        {hazard.existingControls && (
                          <p className="text-sm text-gray-600">
                            <strong>Controls:</strong> {hazard.existingControls.substring(0, 100)}
                            {hazard.existingControls.length > 100 ? '...' : ''}
                          </p>
                        )}
                        <p className="text-sm text-gray-500">
                          <strong>Control Type:</strong> {controlLevel?.label || 'PPE'}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <RiskLevelBadge score={initialScore} />
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <RiskLevelBadge score={residualScore} />
                      </div>
                      {hazard.actionRequired && (
                        <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full">
                          Action Required
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Actions Required Card (if any) */}
      {actionsRequired.length > 0 && (
        <div className="rounded-2xl border border-yellow-200 shadow-sm bg-yellow-50 overflow-hidden">
          <div className="bg-yellow-500 px-5 py-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-white" />
              <h4 className="text-base font-semibold text-white">
                Actions Required ({actionsRequired.length})
              </h4>
            </div>
          </div>
          <div className="divide-y divide-yellow-200">
            {actionsRequired.map((hazard, index) => (
              <div key={hazard.id} className="p-4">
                <p className="font-medium text-gray-900">{hazard.hazardDescription}</p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>Action:</strong> {hazard.actionRequired}
                </p>
                <div className="flex gap-4 mt-2 text-sm text-gray-600">
                  {hazard.actionOwner && (
                    <span><strong>Owner:</strong> {hazard.actionOwner}</span>
                  )}
                  {hazard.actionDeadline && (
                    <span><strong>Deadline:</strong> {hazard.actionDeadline}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review Frequency Card */}
      <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-white" />
            <h4 className="text-base font-semibold text-white">Review Cycle</h4>
          </div>
        </div>
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Review Frequency
          </label>
          <Dropdown
            value={formData.reviewFrequency || 'annually'}
            onChange={(value) => setFormData(prev => ({ ...prev, reviewFrequency: value }))}
            options={Object.entries(REVIEW_FREQUENCIES).map(([key, freq]) => ({
              value: freq.value,
              label: `${freq.label} - ${freq.description}`
            }))}
            width="w-full"
          />
          <p className="mt-2 text-sm text-gray-500">
            This assessment will be reviewed{' '}
            {REVIEW_FREQUENCIES[formData.reviewFrequency]?.label?.toLowerCase() || 'annually'}.
          </p>
        </div>
      </div>

      {/* Submission Checklist */}
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <h4 className="font-medium text-gray-900 mb-3">Pre-Submission Checklist</h4>
        <div className="space-y-2">
          {[
            { check: !!formData.title, label: 'Assessment has a title' },
            { check: hazards.length > 0, label: 'At least one hazard identified' },
            { check: hazards.every(h => h.hazardDescription), label: 'All hazards have descriptions' },
            { check: hazards.every(h => h.existingControls || h.additionalControls), label: 'Control measures defined' },
            {
              check: hazards.filter(h => {
                const score = (h.residualLikelihood || 1) * (h.residualSeverity || 1);
                return score >= 6;
              }).every(h => h.actionRequired),
              label: 'Action plans for ALARP/Intolerable risks'
            }
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              {item.check ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
              )}
              <span className={`text-sm ${item.check ? 'text-gray-700' : 'text-gray-500'}`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Step5_Review;
